import React, {useEffect, useState} from "react";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";

import "./TableFilter.css";
import {StandardButton} from "../../button/standard-button/StandardButton";
import {formatDateAsString, removeCamelCase} from "../../../../util/StringUtil";

export const TableFilter = ({dataToFilter, setFilteredData, hiddenColumns, setCurrentPage, filteredColumns, setFilteredColumns}) => {
    const [showFirstDropdown, setShowFirstDropdown] = useState(false);
    const [showSecondDropdown, setShowSecondDropdown] = useState(false);
    const [uniqueKeyValues, setUniqueKeyValues] = useState(null);
    const [isSelected, setIsSelected] = useState(false);
    const [isNotSelected, setIsNotSelected] = useState(false);
    const [containsSelected, setContainsSelected] = useState(false);
    const [doesNotContainSelected, setDoesNotContainSelected] = useState(false);
    const [filterToApply, setFilterToApply] = useState(null);
    const [keyToFilter, setKeyToFilter] = useState(null);
    const [appliedFilterList, setAppliedFilterList] = useState([]);

    useEffect(() => {
        if (appliedFilterList.length === 2) {
            const [filterOne, filterTwo] = appliedFilterList;
            if (filterOne.filterMethod === filterTwo.filterMethod && filterOne.key === filterTwo.key) {
                const copyOfAppliedFilterList = [...appliedFilterList];
                copyOfAppliedFilterList.splice(0, 1);
                setAppliedFilterList([...copyOfAppliedFilterList]);
            }
        }

        let copyOfDataToFilter = JSON.parse(JSON.stringify(dataToFilter));
        const columnsToBeFiltered = [];
        appliedFilterList.forEach((filter) => {
            const {filterMethod, value, key} = filter;
            if (filterMethod === "is") {
                copyOfDataToFilter = applyIsFilter(copyOfDataToFilter, value, key);
                columnsToBeFiltered.push(key);
            } else if (filterMethod === "isNot") {
                copyOfDataToFilter = applyIsNotFilter(copyOfDataToFilter, value, key);
            } else if (filterMethod === "contains") {
                copyOfDataToFilter = applyContainsFilter(copyOfDataToFilter, value, key);
            } else if (filterMethod === "doesNotContain") {
                copyOfDataToFilter = applyDoesNotContainFilter(copyOfDataToFilter, value, key);
            }
        });
        setFilteredColumns([...columnsToBeFiltered]);
        setCurrentPage(0);
        setFilteredData(copyOfDataToFilter);
    }, [appliedFilterList, dataToFilter]);

    if (!dataToFilter[0]) {
        return null;
    }

    const objectKeys = Object.keys(dataToFilter[0]);

    const applyIsFilter = (data, value, key) => {
        return data.filter((object) => {
            return (object[key].toString() === value);
        });
    }

    const applyIsNotFilter = (data, value, key) => {
        return data.filter((object) => {
            return !(object[key].toString() === value);
        });
    }

    const applyContainsFilter = (data, value, key) => {
        return data.filter((object) => {
            let objectValue = object[key].toString().toLowerCase();
            return objectValue.includes(value.toLowerCase());
        });
    }

    const applyDoesNotContainFilter = (data, value, key) => {
        return data.filter((object) => {
            let objectValue = object[key].toString().toLowerCase();
            return !objectValue.includes(value.toLowerCase());
        });
    }

    const handleAddFilterClick = () => {
        setFilterToApply(null);
        if (!showFirstDropdown && !showSecondDropdown) {
            setShowFirstDropdown(true);
        } else {
            setShowFirstDropdown(false);
            setShowSecondDropdown(false);
        }
    }

    const handleKeyClick = (key) => {
        setKeyToFilter(key);
        let filteredValues = [];
        dataToFilter.forEach((object) => {
            if (filteredValues.indexOf(object[key]) === -1) {
                filteredValues.push(object[key]);
            }
        });

        if (typeof filteredValues[0] === "string") {
            filteredValues.sort((value1, value2) => {
                if (value1.toLowerCase() < value2.toLowerCase()) {
                    return -1
                }
                if (value1.toLowerCase() > value2.toLowerCase()) {
                    return 1
                }
                return 0;
            });
        }

        const mappedValues = filteredValues.map((value, index) => {
            if (typeof filteredValues[0] === "string") {
                if (value.includes("000+00")) {
                    return {value: index, label: formatDateAsString(new Date(value))}
                }
            }
            return {value: index, label: value.toString()}
        });

        setUniqueKeyValues(mappedValues);
        setShowFirstDropdown(false);
        setShowSecondDropdown(true);
    }

    const toggleFilterMode = (filterMethod) => {
        setIsSelected(false);
        setIsNotSelected(false);
        setContainsSelected(false);
        setDoesNotContainSelected(false);

        if (filterMethod === "is") {
            setIsSelected(true);
        } else if (filterMethod === "isNot") {
            setIsNotSelected(true);
        } else if (filterMethod === "contains") {
            setContainsSelected(true);
        } else if (filterMethod === "doesNotContain") {
            setDoesNotContainSelected(true);
        }
    }

    const handleChange = (filterMethod, value) => {
        setFilterToApply({filterMethod, value, key: keyToFilter});
    }

    const handleApplyFilterClick = () => {
        setAppliedFilterList((prevState) => ([...prevState, filterToApply]));
        setShowSecondDropdown(false);
        setIsSelected(false);
        setIsNotSelected(false);
        setContainsSelected(false);
        setDoesNotContainSelected(false);
    }

    const handleRemoveFilterClick = (filter, index) => {
        const copyOfFilteredColumns = [...filteredColumns];
        filteredColumns.splice(copyOfFilteredColumns.indexOf(filter.key), 1);
        setFilteredColumns([...copyOfFilteredColumns]);

        const copyOfAppliedFilterList = [...appliedFilterList];
        copyOfAppliedFilterList.splice(index, 1);
        setAppliedFilterList([...copyOfAppliedFilterList]);
    }

    const firstDropdown = (
        <div className="table-filter-dropdown">
            {objectKeys.map((key, index) => {
                if (!hiddenColumns.includes(key) && !key.toLowerCase().includes('date')) {
                    return (
                        <div className="table-filter-dropdown-one-item" key={index} onClick={() => {
                            handleKeyClick(key)
                        }}>
                            {removeCamelCase(key)}
                        </div>
                    );
                } else {
                    return null;
                }
            })}
        </div>
    );

    const secondDropdown = (
        <div className="table-filter-dropdown">
            <div className="table-filter-dropdown-two-input-container">
                <div className="table-filter-dropdown-two-input-container-text" onClick={() => {
                    toggleFilterMode("is")
                }}>
                    <div className={isSelected ? "fill-circle" : ""}/>
                    <p>Is</p>
                </div>
                {isSelected && <Select options={uniqueKeyValues}
                                       onChange={value => handleChange("is", value.label)}
                                       classNamePrefix="select"/>}
            </div>
            <div className="table-filter-dropdown-two-input-container">
                <div className="table-filter-dropdown-two-input-container-text" onClick={() => {
                    toggleFilterMode("isNot")
                }}>
                    <div className={isNotSelected ? "fill-circle" : ""}/>
                    <p>Is Not</p>
                </div>
                {isNotSelected && <Select options={uniqueKeyValues}
                                          onChange={value => handleChange("isNot", value.label)}
                                          classNamePrefix="select"/>}
            </div>
            <div className="table-filter-dropdown-two-input-container">
                <div className="table-filter-dropdown-two-input-container-text" onClick={() => {
                    toggleFilterMode("contains")
                }}>
                    <div className={containsSelected ? "fill-circle" : ""}/>
                    <p>Contains</p>
                </div>
                {containsSelected && <input className="text-input"
                                            type="text"
                                            maxLength={20}
                                            onChange={event => handleChange("contains", event.target.value)}/>}
            </div>
            <div className="table-filter-dropdown-two-input-container">
                <div className="table-filter-dropdown-two-input-container-text" onClick={() => {
                    toggleFilterMode("doesNotContain")
                }}>
                    <div className={doesNotContainSelected ? "fill-circle" : ""}/>
                    <p>Does Not Contain</p>
                </div>
                {doesNotContainSelected && <input className="text-input"
                                                  type="text"
                                                  maxLength={20}
                                                  onChange={event => handleChange("doesNotContain", event.target.value)}/>}
            </div>
            <StandardButton text="Apply Filter" onClick={handleApplyFilterClick} disabled={!filterToApply}/>
        </div>
    );

    const appliedFilters = appliedFilterList.map((filter, index) => {
        return (
            <div className="filter-container" key={index}>
                <div className="filter-container-text-container">
                    <div className="filter-container-top-line">
                        {removeCamelCase(filter.key) + " "}
                        {removeCamelCase(filter.filterMethod) + ":"}
                    </div>
                    <div className="filter-container-bottom-line">
                        {filter.value}
                    </div>
                </div>
                <div className="filter-container-button-container"
                     onClick={() => handleRemoveFilterClick(filter, index)}>
                    <FontAwesomeIcon icon={faTimes}/>
                </div>
            </div>
        );
    })

    return (
        <div className="table-filter-container">
            <div className="table-filter-container-applied-filters">
                <StandardButton text="Add Filter" disabled={appliedFilterList.length > 1}
                                onClick={handleAddFilterClick}/>
                {appliedFilters}
            </div>
            {showFirstDropdown && firstDropdown}
            {showSecondDropdown && secondDropdown}
        </div>
    );
}
